import {Brick, registerBrick} from 'olympe';
import {checkLabelValueObject} from '../checkObjects';
import {accountTypeOptions} from '../../components/_helpers';
import {isAddressEmpty} from '../address';

/**
 * This brick merge the default values of a user into a PR JSON
 */
export default class MergeUserDefaults extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {string} pRJSONSource
     * @param {*} userDefaults
     * @param {function()} forwardEvent
     * @param {function(string)} setMergedPRJSON
     */
    update($, [pRJSONSource, userDefaults], [forwardEvent, setMergedPRJSON]) {
        // Write your code here. You have to implement this method !
        // Executed every time the brick receives a new trigger event, override `setupExecution()` to change the behavior.

        // Guards
        if (typeof pRJSONSource !== 'string') {
            console.error('Merge User Defaults: Provided source is not a string');
            return;
        }
        if (typeof userDefaults !== 'object') {
            console.error('Merge User Defaults: Provided userDefaults is not an object');
            return;
        }

        // If there is no User Defaults (if a user creates a request before getting connected)
        if (userDefaults === null) {
            setMergedPRJSON(pRJSONSource);
            forwardEvent();
            return;
        }

        let obj;
        try {
            obj = JSON.parse(pRJSONSource);
        } catch (e) {
            console.error('Merge User Defaults: Error when parsing source: '+e.message);
            return;
        }

        // Use the following maps to configure which properties should be updated
        // The maps keys are the user default properties (from PG)
        // The maps values are the properties in the PR JSON
        const rootMapping = new Map();
        rootMapping.set('spender', 'spender');
        rootMapping.set('requester', 'requester');

        const headerDetailsMapping = new Map();
        headerDetailsMapping.set('storage_location', 'defaultStorageLocation');
        headerDetailsMapping.set('purchasing_group', 'purchasingGroup');
        headerDetailsMapping.set('purchase_organization', 'purchaseOrganization');

        // If there is no company code from the API, or if there are multiple company codes, but one of them match the user default one,
        // use the user default value. Otherwise, do not use it.
        if ((!Array.isArray(obj.headerDetails.pslCompanyCodes) || obj.headerDetails.pslCompanyCodes.length === 0) ||
            (Array.isArray(obj.headerDetails.pslCompanyCodes) &&
                obj.headerDetails.pslCompanyCodes
                    .map((cc) => cc.value)
                    .includes(checkLabelValueObject(userDefaults.company_code).value))) {
            headerDetailsMapping.set('company_code', 'companyCode');
        }

        // If there is no plant code from the API, or if there are multiple plant codes, but one of them match the user default one,
        // use the user default value. Otherwise, do not use it.
        if ((!Array.isArray(obj.headerDetails.plants) || obj.headerDetails.plants.length === 0) ||
            (Array.isArray(obj.headerDetails.plants) &&
                obj.headerDetails.plants
                    .map((plant) => plant.value)
                    .includes(checkLabelValueObject(userDefaults.plant).value))) {
            headerDetailsMapping.set('plant', 'defaultPlant');
        }

        const financialAccountsMapping = new Map();
        financialAccountsMapping.set('gl_number', 'glNumber');
        financialAccountsMapping.set('account_type', 'accountType');
        financialAccountsMapping.set('wbs_project', 'wbs');
        financialAccountsMapping.set('cost_center', 'costCenter');
        financialAccountsMapping.set('profit_center', 'profitCenter');

        const materialsMapping = new Map();
        materialsMapping.set('tax_code', 'taxCode');
        materialsMapping.set('currency', 'currency');
        materialsMapping.set('plant', 'plant');
        materialsMapping.set('storage_location', 'storageLocation');

        const withoutLabelValue = [];

        const userDefaultDeliveryAddress = userDefaults['delivery_address'];
        if (userDefaultDeliveryAddress && obj.headerDetails?.defaultDeliveryAddress?.street === '' ) {
            try {
                obj.headerDetails.defaultDeliveryAddress = JSON.parse(userDefaultDeliveryAddress);
            } catch (e) {
                console.log('Parsing delivery address failed');
                console.log(e.message);
            }
        }

        if (userDefaultDeliveryAddress && obj.materials.some((m) => isAddressEmpty(m.deliveryAddress))) {
            try {
                obj.materials.forEach((m) => {
                    if (isAddressEmpty(m.deliveryAddress)) {
                        m.deliveryAddress = JSON.parse(userDefaultDeliveryAddress);
                    }
                });
            } catch (e) {
                console.log('Parsing delivery address failed');
                console.log(e.message);
            }
        }

        rootMapping.forEach((objProperty, userDefaultsProperty) => {
            this.applyDefaultIfEmpty(obj, objProperty, userDefaults, userDefaultsProperty);
        });

        headerDetailsMapping.forEach((objProperty, userDefaultsProperty) => {
            this.applyDefaultIfEmpty(obj.headerDetails, objProperty, userDefaults, userDefaultsProperty, withoutLabelValue.includes(userDefaultsProperty));
        });
        if (obj.financialInfo && obj.financialInfo.accounts && Array.isArray(obj.financialInfo.accounts)) {
            financialAccountsMapping.forEach((objProperty, userDefaultsProperty) => {
                obj.financialInfo.accounts.forEach((account) => {
                    this.applyDefaultIfEmpty(account, objProperty, userDefaults, userDefaultsProperty, withoutLabelValue.includes(userDefaultsProperty));
                });
            });
        }

        if (obj.materials && Array.isArray(obj.materials)) {
            materialsMapping.forEach((objProperty, userDefaultsProperty) => {
                obj.materials.forEach((material) => {
                    this.applyDefaultIfEmpty(material, objProperty, userDefaults, userDefaultsProperty, withoutLabelValue.includes(userDefaultsProperty));
                });
            });
        }

        setMergedPRJSON(JSON.stringify(obj));
        forwardEvent();
    }

    /**
     * Update the `obj` with the default value if the current value is undefined or an empty string
     * This method changes the `obj` and return void.
     *
     * @private
     * @param {object} obj - The part of the PR JSON that should be looked into for the given `property`
     * @param {string} objProperty - Property which should be set with default value if undefined or empty
     * @param {object} userDefaults - User Defaults object
     * @param {string} userDefaultsProperty - User Defaults property that is relative to the `objProperty`
     * @param {boolean=} [withoutLabelValue=false] - If true, the default will be applied directly to the property, if false (default), the property is considered as an object with `label` and `value` properties.
     */
    applyDefaultIfEmpty(obj, objProperty, userDefaults, userDefaultsProperty, withoutLabelValue = false) {
        if (typeof obj !== 'object' || obj === null) {
            return;
        }
        if (typeof objProperty !== 'string') {
            return;
        }
        if (typeof userDefaults !== 'object' || userDefaults === null) {
            return;
        }
        if (typeof userDefaultsProperty !== 'string') {
            return;
        }
        const currentValue = obj[objProperty];
        let defaultValue = userDefaults[userDefaultsProperty];
        if (defaultValue === undefined || defaultValue === null) {
            return;
        }

        if (!withoutLabelValue) {
            const valueShouldBeSet = !currentValue || typeof currentValue !== 'object' || !currentValue.value;
            if (valueShouldBeSet) {
                if (objProperty === 'currency') {
                    defaultValue = defaultValue.split(' - ')[0];
                    obj[objProperty] = checkLabelValueObject(defaultValue);
                } else if (objProperty === 'accountType') {
                    defaultValue = accountTypeOptions.find((option) => option.label === defaultValue);
                    obj[objProperty] = checkLabelValueObject(defaultValue);
                } else if ((objProperty === 'spender' || objProperty === 'requester')) {
                    if (defaultValue === '{}' || !defaultValue) {
                        obj[objProperty] = {label: '', value: '', email: defaultValue.email};
                    } else {
                        obj[objProperty] = JSON.parse(defaultValue);
                    }
                } else {
                    obj[objProperty] = checkLabelValueObject(defaultValue);
                }
            }
        } else {
            const valueShouldBeSet = currentValue === undefined || currentValue === '';
            if (valueShouldBeSet) {
                obj[objProperty] = defaultValue;
            }
        }
    }
}

registerBrick('018041eaed97c7ee6a3b', MergeUserDefaults);
